import { schemeSet2, schemeTableau10 } from "d3";
import { roundCm, type GradingBucketDraft } from "@/utils/gradingUtils";

// Re-exported so the cm-rounding rule has a single source of truth (gradingUtils)
// while callers of this module can still import it from here.
export { roundCm };

/**
 * Number-line ("Caliper Line") model for the image-measurement grade editor.
 *
 * A grading config is a partition of `[0, ∞)` into N contiguous grades. The only
 * real degrees of freedom are the N-1 internal CUT POINTS (each shared by the grade
 * below — its open upper edge — and the grade above — its closed lower edge). Modelling
 * the editor this way makes gaps and overlaps structurally impossible.
 *
 * This module holds the pure math (cm<->px mapping, snap, clamp) and the conversion
 * to/from the persisted `GradingBucketDraft[]` shape, so the component stays a thin
 * controlled view and the logic is unit-testable without browser layout.
 */

export interface GradingLineGrade {
  id: string;
  label: string;
}

export interface GradingLineModel {
  /** Ordered grade names, smallest first. Length N. */
  grades: GradingLineGrade[];
  /** Internal cut points in cm, strictly increasing. Length N-1. */
  cuts: number[];
}

/** Drag snaps to 1mm (0.1cm); typing allows finer values rounded to 4dp. */
export const DRAG_STEP_CM = 0.1;
/** Drag/keyboard step when the editor is displaying inches. */
export const DRAG_STEP_IN = 0.05;
export const CM_PER_INCH = 2.54;

/**
 * Display unit for the grade editor. The model and persisted config are ALWAYS cm
 * (then meters at save); this only changes how values are shown and entered, so a
 * user with inch-based grade specs can type inches without converting by hand.
 */
export type GradingDisplayUnit = "cm" | "in";

/** cm -> the chosen display unit (numeric). */
export function cmToUnit(cm: number, unit: GradingDisplayUnit): number {
  return unit === "in" ? cm / CM_PER_INCH : cm;
}

/** A value entered in `unit` -> cm (rounded to the grading cm precision, 4dp). */
export function unitToCm(value: number, unit: GradingDisplayUnit): number {
  return roundCm(unit === "in" ? value * CM_PER_INCH : value);
}

/**
 * Format a cm value for display in `unit`, showing up to 4 decimals (min 2) so
 * inch standards like 1.0625 in render in full instead of being truncated to 1.06.
 * Trailing zeros beyond the 2-decimal minimum are trimmed (2.70, 1.062, 1.0625).
 */
export function formatUnit(cm: number, unit: GradingDisplayUnit): string {
  const value = cmToUnit(cm, unit);
  const trimmed = value.toFixed(4).replace(/0+$/, "");
  const decimals = trimmed.split(".")[1]?.length ?? 0;
  return decimals < 2 ? value.toFixed(2) : trimmed;
}

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

/** Finest fraction the grade display renders (1/32 in). */
const INCH_FRACTION_DENOMINATOR = 32;

/**
 * Render an inch value the way growers and processors write produce sizes: a
 * canonical mixed fraction reduced to lowest terms, with a power-of-two
 * denominator up to 1/32 and a trailing inch mark. The value is rounded to the
 * nearest 1/32 in first, then the fractional part collapses to 1/16, 1/8, 1/4
 * or 1/2 whenever the numerator allows.
 *
 *   0.75 -> 3/4"   1.0625 -> 1 1/16"   2 -> 2"   2.125 -> 2 1/8"   0.03125 -> 1/32"
 */
export function formatInchesFraction(inches: number): string {
  // Guard non-finite input: a NaN/Infinity would make the gcd reduction recurse
  // forever. Degrade to blank so a missing measurement never crashes the editor.
  if (!Number.isFinite(inches)) return "";
  const totalParts = Math.round(inches * INCH_FRACTION_DENOMINATOR);
  const whole = Math.floor(totalParts / INCH_FRACTION_DENOMINATOR);
  const remainder = totalParts - whole * INCH_FRACTION_DENOMINATOR;
  if (remainder === 0) return `${whole}"`;
  const divisor = gcd(remainder, INCH_FRACTION_DENOMINATOR);
  const fraction = `${remainder / divisor}/${INCH_FRACTION_DENOMINATOR / divisor}`;
  return whole === 0 ? `${fraction}"` : `${whole} ${fraction}"`;
}

/**
 * Convenience for the grade editor, whose line model stores cm: convert cm to
 * inches and render the canonical fraction.
 */
export function cmToInchFraction(cm: number): string {
  return formatInchesFraction(cmToUnit(cm, "in"));
}

/** Snap a raw cm value to the display-unit grid (1 mm in cm mode, 0.05 in in inch mode). */
export function snapDisplayCm(cm: number, unit: GradingDisplayUnit): number {
  if (unit === "in") {
    const snappedIn = Math.round(cm / CM_PER_INCH / DRAG_STEP_IN) * DRAG_STEP_IN;
    return roundCm(snappedIn * CM_PER_INCH);
  }
  return snapCm(cm, DRAG_STEP_CM);
}
/** Smallest spacing between adjacent cuts so a grade can never be zero-width. */
export const MIN_GAP_CM = 0.01;
/** Default cut placed when grading is first seeded. */
export const SEED_CUT_CM = 1;

/** Snap a cm value to the nearest `stepCm` multiple, rounded to 2dp. */
export function snapCm(cm: number, stepCm: number): number {
  if (stepCm <= 0) return roundCm(cm);
  return roundCm(Math.round(cm / stepCm) * stepCm);
}

/**
 * Clamp cut `index` so it stays strictly between its neighbours (with `minGapCm`
 * spacing) and above 0. The first cut's floor is `minGapCm`; the last cut has no
 * ceiling (the top grade is unbounded).
 */
export function clampCut(
  cuts: number[],
  index: number,
  valueCm: number,
  minGapCm: number = MIN_GAP_CM
): number {
  const lower = index === 0 ? minGapCm : roundCm(cuts[index - 1] + minGapCm);
  const upper =
    index === cuts.length - 1 ? Infinity : roundCm(cuts[index + 1] - minGapCm);
  // `lower` is always >= minGapCm, so clamping to `lower` keeps the cut positive.
  let v = valueCm;
  if (v < lower) v = lower;
  if (upper !== Infinity && v > upper) v = upper;
  return roundCm(v);
}

/** The unbounded top grade occupies this fraction of the ruler — just enough for its label. */
export const TOP_GRADE_AXIS_FRACTION = 0.18;

/**
 * Visible axis maximum. The bounded grades fill `1 - TOP_GRADE_AXIS_FRACTION` of the
 * ruler and the unbounded top grade takes only the small remainder, so the bounded
 * grades get the most room (rather than the open-ended grade hogging a third of it).
 */
export function axisMaxCm(cuts: number[]): number {
  const lastCut = cuts.length ? cuts[cuts.length - 1] : 1;
  return roundCm(lastCut / (1 - TOP_GRADE_AXIS_FRACTION));
}

/** cm -> percentage of the axis width, clamped to [0, 100]. */
export function cmToPct(cm: number, maxCm: number): number {
  if (maxCm <= 0) return 0;
  return Math.max(0, Math.min(100, (cm / maxCm) * 100));
}

/** Pointer clientX -> cm, given the bar's left edge and width. Clamped to the bar. */
export function pxToCm(
  clientX: number,
  rectLeft: number,
  rectWidth: number,
  maxCm: number
): number {
  if (rectWidth <= 0) return 0;
  const ratio = Math.max(0, Math.min(1, (clientX - rectLeft) / rectWidth));
  return ratio * maxCm;
}

interface Interval {
  lo: number;
  hi: number; // Infinity for the top grade
  grade: GradingLineGrade;
}

const draftToInterval = (draft: GradingBucketDraft): Interval | null => {
  const grade = { id: draft.id, label: draft.label };
  switch (draft.kind) {
    case "lessThan":
      return draft.maxCm !== undefined
        ? { lo: 0, hi: draft.maxCm, grade }
        : null;
    case "between":
      return draft.minCm !== undefined && draft.maxCm !== undefined
        ? { lo: draft.minCm, hi: draft.maxCm, grade }
        : null;
    case "greaterThanOrEqual":
      return draft.minCm !== undefined
        ? { lo: draft.minCm, hi: Infinity, grade }
        : null;
  }
};

/**
 * Convert persisted drafts (a contiguous tiling produced by this editor or hydrated
 * from a saved grading) into the line model. Boundaries are sorted defensively.
 */
export function draftsToLineModel(
  drafts: GradingBucketDraft[]
): GradingLineModel {
  const intervals = drafts
    .map(draftToInterval)
    .filter((iv): iv is Interval => iv !== null)
    .sort((a, b) => a.lo - b.lo);

  const grades = intervals.map((iv) => iv.grade);
  const cuts: number[] = [];
  for (let i = 0; i < intervals.length - 1; i += 1) {
    cuts.push(roundCm(intervals[i].hi));
  }
  return { grades, cuts };
}

/** Inverse of {@link draftsToLineModel}, preserving grade ids. */
export function lineModelToDrafts(
  model: GradingLineModel
): GradingBucketDraft[] {
  const { grades, cuts } = model;
  const n = grades.length;
  if (n === 0) return [];
  if (n === 1) {
    // Single grade has no cut; not a valid saved shape (parent blocks <2 grades),
    // emit a positive-floored greaterThanOrEqual so the validator surfaces it.
    return [{ id: grades[0].id, label: grades[0].label, kind: "greaterThanOrEqual", minCm: 0 }];
  }
  return grades.map((grade, i) => {
    if (i === 0) {
      return { id: grade.id, label: grade.label, kind: "lessThan", maxCm: cuts[0] };
    }
    if (i === n - 1) {
      return {
        id: grade.id,
        label: grade.label,
        kind: "greaterThanOrEqual",
        minCm: cuts[n - 2],
      };
    }
    return {
      id: grade.id,
      label: grade.label,
      kind: "between",
      minCm: cuts[i - 1],
      maxCm: cuts[i],
    };
  });
}

/** Generate a label not already present (case/space-insensitive): "Grade", "Grade 2", ... */
export function uniqueLabel(base: string, existing: string[]): string {
  const taken = new Set(existing.map((s) => s.trim().toLowerCase()));
  if (!taken.has(base.trim().toLowerCase())) return base;
  let n = 2;
  while (taken.has(`${base} ${n}`.trim().toLowerCase())) n += 1;
  return `${base} ${n}`;
}

/**
 * Default name for a newly added grade: "Grade N", where N is one past the highest
 * existing "Grade N" (so seeding/splitting keeps incrementing — Grade 1, Grade 2,
 * Grade 3, …). Renamed grades are ignored, so the counter never collides with them.
 */
export function nextGradeLabel(existing: string[]): string {
  let maxN = 0;
  for (const label of existing) {
    const m = /^grade\s+(\d+)$/i.exec(label.trim());
    if (m) maxN = Math.max(maxN, parseInt(m[1], 10));
  }
  return `Grade ${maxN + 1}`;
}

/**
 * Split grade `index` at its midpoint, inserting a new cut and a new grade. The
 * unbounded top grade splits using axis headroom so the new cut lands at a sensible spot.
 */
export function splitGradeAt(
  model: GradingLineModel,
  index: number,
  makeId: () => string
): GradingLineModel {
  const { grades, cuts } = model;
  const n = grades.length;
  const lo = index === 0 ? 0 : cuts[index - 1];
  const hi = index === n - 1 ? Infinity : cuts[index];

  // A bounded grade must be at least 2*MIN_GAP wide to fit a new cut without
  // colliding with (or crossing) a neighbour after rounding. If it isn't, the
  // split is a no-op rather than producing a duplicate / zero-width cut.
  if (hi !== Infinity && roundCm(hi - lo) < 2 * MIN_GAP_CM) {
    return model;
  }

  let mid =
    hi === Infinity
      ? roundCm(lo + Math.max(0.5, (axisMaxCm(cuts) - lo) / 2))
      : roundCm((lo + hi) / 2);
  if (hi !== Infinity) {
    mid = Math.min(
      Math.max(mid, roundCm(lo + MIN_GAP_CM)),
      roundCm(hi - MIN_GAP_CM)
    );
  }

  const newCuts = [...cuts];
  newCuts.splice(index, 0, mid);
  const newGrades = [...grades];
  newGrades.splice(index + 1, 0, {
    id: makeId(),
    label: nextGradeLabel(grades.map((g) => g.label)),
  });
  return { grades: newGrades, cuts: newCuts };
}

/** Remove grade `index` by dropping its bordering cut. Refuses to drop below 2 grades. */
export function removeGrade(
  model: GradingLineModel,
  index: number
): GradingLineModel {
  const { grades, cuts } = model;
  const n = grades.length;
  if (n <= 2) return model;
  const cutToDrop =
    index === 0 ? 0 : index === n - 1 ? cuts.length - 1 : index;
  return {
    grades: grades.filter((_, i) => i !== index),
    cuts: cuts.filter((_, i) => i !== cutToDrop),
  };
}

/**
 * Initial state when the user first opens grading: two grades with default names
 * ("Grade 1", "Grade 2") and one cut. Default names mean the config is valid out of
 * the box — the user renames in place rather than being blocked by a "name required".
 */
export function seedLineModel(
  makeId: () => string,
  cutCm: number = SEED_CUT_CM
): GradingLineModel {
  return {
    grades: [
      { id: makeId(), label: "Grade 1" },
      { id: makeId(), label: "Grade 2" },
    ],
    cuts: [roundCm(cutCm)],
  };
}

/**
 * Color for grade `index`, mirroring the heatmap legend
 * (D3HeatmapGrid.tsx: schemeSet2 for <=8 buckets, else schemeTableau10, by config order)
 * so a grade is the same color in the editor and on the heatmap.
 */
export function colorForGradeIndex(index: number, count: number): string {
  const palette = count <= 8 ? schemeSet2 : schemeTableau10;
  return palette[index % palette.length];
}
